/**
 * useRevealWindow — true for a short window after the screen stops being busy
 * (loading or initializing), so the cards that mount with the fresh results
 * morph out of their placeholders (components/MorphReveal).
 *
 * Computed during render, not in an effect: the first cards mount in the same
 * render that flips busy → false, and an effect would set the flag one render
 * too late for them.
 */
import { useRef, useState, useEffect } from 'react';
import { REVEAL_WINDOW_MS } from '../utils/loadingPhase';

export function nextRevealState(prevBusy, busy, endedAt, now) {
  const justFinished = !!prevBusy && !busy;
  const nextEndedAt = justFinished ? now : endedAt;
  const active = !busy && nextEndedAt > 0 && now - nextEndedAt < REVEAL_WINDOW_MS;
  return { endedAt: nextEndedAt, active };
}

export function useRevealWindow(busy) {
  const prevBusyRef = useRef(busy);
  const endedAtRef = useRef(0);
  const { endedAt, active } = nextRevealState(prevBusyRef.current, busy, endedAtRef.current, Date.now());
  endedAtRef.current = endedAt;
  prevBusyRef.current = busy;

  // Re-render once the window closes so `active` drops back to false.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const t = setTimeout(() => setTick((n) => n + 1), REVEAL_WINDOW_MS);
    return () => clearTimeout(t);
  }, [active, endedAt]);

  return active;
}
